import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import anime from 'animejs';
import { IconLoader } from '@components/icons';
import styled from 'styled-components';
import { theme, mixins } from '@styles';
const { colors } = theme;

const LoaderContainer = styled.div`
  ${mixins.flexCenter};
  background-color: ${colors.darkNavy};
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 99;
`;
const LogoWrapper = styled.div`
  width: max-content;
  max-width: 150px;
  transition: ${theme.transition};
  opacity: ${props => (props.isMounted ? 1 : 0)};
  svg {
    width: 100%;
    height: 100%;
    display: block;
    margin: 0 auto;
    user-select: none;
    path {
      opacity: 0;
    }
  }
`;

const Loader = ({ finishLoading }) => {
  const animate = () => {
    const loader = anime.timeline({
      complete: () => finishLoading(),
    });

    const animations = [
      // 1. Gentle Fade and Slide Up
      () => {
        loader.add({
          targets: '#logo path',
          delay: anime.stagger(10),
          duration: 1500,
          easing: 'easeOutQuart',
          translateY: [20, 0],
          opacity: [0, 1],
        });
      },
      // 2. Elegant Scale Down and Fade
      () => {
        loader.add({
          targets: '#logo path',
          delay: anime.stagger(15, { from: 'center' }),
          duration: 1800,
          easing: 'easeOutExpo',
          scale: [1.1, 1],
          opacity: [0, 1],
        });
      },
      // 3. Fade and Slide Down
      () => {
        loader.add({
          targets: '#logo path',
          delay: anime.stagger(10, { direction: 'reverse' }),
          duration: 1500,
          easing: 'easeOutQuart',
          translateY: [-20, 0],
          opacity: [0, 1],
        });
      },
      // 4. Subtle Expand and Fade
      () => {
        loader.add({
          targets: '#logo path',
          delay: anime.stagger(10),
          duration: 1500,
          easing: 'easeOutSine',
          scale: [0.95, 1],
          opacity: [0, 1],
        });
      },
      // 5. Sweep from Left
      () => {
        loader.add({
          targets: '#logo path',
          delay: anime.stagger(10, { from: 'first' }),
          duration: 1500,
          easing: 'easeOutQuart',
          translateX: [-20, 0],
          opacity: [0, 1],
        });
      },
      // 6. Color Parts Sequential
      () => {
        loader
          .add({
            targets: '.logo-part-1',
            duration: 800,
            easing: 'easeInOutSine',
            opacity: [0, 1],
          })
          .add(
            {
              targets: '.logo-part-2',
              duration: 800,
              easing: 'easeInOutSine',
              opacity: [0, 1],
            },
            '-=400',
          )
          .add(
            {
              targets: '.logo-part-4',
              duration: 800,
              easing: 'easeInOutSine',
              opacity: [0, 1],
            },
            '-=400',
          );
      },
      // 7. Subtle Rotate In
      () => {
        loader.add({
          targets: '#logo path',
          delay: anime.stagger(10),
          duration: 1500,
          easing: 'easeOutCirc',
          rotate: [-5, 0],
          opacity: [0, 1],
        });
      },
      // 8. Random Sparkle Fade In
      () => {
        loader.add({
          targets: '#logo path',
          delay: anime.stagger(20, { from: 'random' }),
          duration: 1200,
          easing: 'easeInOutQuad',
          opacity: [0, 1],
        });
      },
      // 9. Soft Elastic Pop
      () => {
        loader.add({
          targets: '#logo path',
          delay: anime.stagger(15),
          duration: 1600,
          easing: 'easeOutElastic(1, .8)',
          scale: [0.8, 1],
          opacity: [0, 1],
        });
      },
      // 10. Sweep from Right
      () => {
        loader.add({
          targets: '#logo path',
          delay: anime.stagger(10, { from: 'last' }),
          duration: 1500,
          easing: 'easeOutQuart',
          translateX: [20, 0],
          opacity: [0, 1],
        });
      },
    ];

    // Pick a random animation to play
    const randomIndex = Math.floor(Math.random() * animations.length);
    animations[randomIndex]();

    // Common Exit Animation
    loader
      .add({
        targets: '#logo',
        delay: 700,
        duration: 300,
        easing: 'easeInOutQuart',
        opacity: 0,
        scale: 0.1,
      })
      .add({
        targets: '.loader',
        duration: 200,
        easing: 'easeInOutQuart',
        opacity: 0,
        zIndex: -1,
      });
  };

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 10);
    animate();
    return () => clearTimeout(timeout);
  }, []);

  return (
    <LoaderContainer className="loader">
      <Helmet bodyAttributes={{ class: `hidden` }} />

      <LogoWrapper isMounted={isMounted}>
        <IconLoader />
      </LogoWrapper>
    </LoaderContainer>
  );
};

Loader.propTypes = {
  finishLoading: PropTypes.func.isRequired,
};

export default Loader;
